import React, { useMemo } from "react";
import { Box, Tooltip } from "@mui/material";
import { safeJSONParse } from "../../../utils/helper";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import styles from "../agent-response.module.scss";
import markdownStyles from "../../MarkdownRenderer/markdown.module.css";

type TranslationAgentProps = {
  parsedContent: any;
  handleSuggestionClick: (suggestion: string) => void;
};

const ARABIC_REGEX = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;

// Custom Mark component for highlighting with tooltip
const HighlightedMark = ({ node, ...props }: any) => {
  const reason = props["data-reason"] || "";
  const children = props.children;

  return (
    <Tooltip title={reason} arrow placement="top">
      <Box
        component="mark"
        sx={{
          backgroundColor: "#c8f0de",
          padding: "2px 0",
          cursor: reason ? "help" : "default",
          border: "none",
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

export default function TranslationAgent({
  parsedContent,
  handleSuggestionClick,
}: TranslationAgentProps): JSX.Element {
  const finalAnswer = safeJSONParse(parsedContent?.finalAnswer);
  const question = parsedContent?.question;
  const text = finalAnswer?.text || "";
  const suggestions = finalAnswer?.suggestions || [];

  const textDirection = useMemo(
    () => (ARABIC_REGEX.test(text) ? "rtl" : "ltr"),
    [text]
  );

  return (
    <>
      <Box>
        <Box
          className={markdownStyles.markdown}
          sx={{
            direction: textDirection,
            textAlign: textDirection === "rtl" ? "right" : "left",
            "& p": { margin: "0.5em 0" },
            "& h1, & h2, & h3": { marginTop: "1em", marginBottom: "0.5em" },
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={
              {
                mark: HighlightedMark,
                h1: ({ node, ...props }) => (
                  <h1 className={markdownStyles.h1} {...props} />
                ),
                h2: ({ node, ...props }) => (
                  <h2 className={markdownStyles.h2} {...props} />
                ),
                h3: ({ node, ...props }) => (
                  <h3 className={markdownStyles.h3} {...props} />
                ),
                h4: ({ node, ...props }) => (
                  <h4 className={markdownStyles.h4} {...props} />
                ),
                ul: ({ node, ...props }) => (
                  <ul className={markdownStyles.ul} {...props} />
                ),
                ol: ({ node, ...props }) => (
                  <ol className={markdownStyles.ol} {...props} />
                ),
                li: ({ node, ...props }) => (
                  <li className={markdownStyles.li} {...props} />
                ),
                p: ({ node, ...props }) => (
                  <p className={markdownStyles.p} {...props} />
                ),
                table: ({ node, ...props }) => (
                  <table className={markdownStyles.table} {...props} />
                ),
                thead: ({ node, ...props }) => (
                  <thead className={markdownStyles.thead} {...props} />
                ),
                tbody: ({ node, ...props }) => (
                  <tbody className={markdownStyles.tbody} {...props} />
                ),
                tr: ({ node, ...props }) => (
                  <tr className={markdownStyles.tr} {...props} />
                ),
                th: ({ node, ...props }) => (
                  <th className={markdownStyles.th} {...props} />
                ),
                td: ({ node, ...props }) => (
                  <td className={markdownStyles.td} {...props} />
                ),
                strong: ({ node, ...props }) => (
                  <strong className={markdownStyles.strong} {...props} />
                ),
                em: ({ node, ...props }) => (
                  <em className={markdownStyles.em} {...props} />
                ),
              } as Components
            }
          >
            {text}
          </ReactMarkdown>
        </Box>
      </Box>

      {suggestions && suggestions.length > 0 && (
        <div className={styles.suggestionsContainer}>
          {suggestions.map((suggestion: string, idx: number) => (
            <div
              className={styles.suggestionBox}
              key={idx}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <p>{suggestion}</p>
            </div>
          ))}
        </div>
      )}

      <div className={styles.finalQuestion}>{question}</div>
    </>
  );
}
